import costOfLivingData from '@/data/costOfLiving.json';
import { fetchLiveExchangeRates, type ExchangeRates as LiveExchangeRates } from './exchangeRateService';

export interface City {
  name: string;
  country: string;
  currency: string;
  symbol: string;
  averageSalary: number;
  taxRate: number;
  region: string;
  categories: {
    [category: string]: {
      [tier: string]: [number, number];
    };
  };
}

export interface CategoryBreakdown {
  fromCost: number;        // local currency of fromCity
  toCost: number;         // local currency of toCity
  fromCostUSD: number;    // USD normalized value
  toCostUSD: number;      // USD normalized value
  percentChange: number;  // computed using USD-normalized values
}

export interface SalaryComparison {
  fromSalary: number;          // local currency of fromCity
  toSalary: number;            // local currency of toCity
  fromAfterTax: number;        // local currency of fromCity
  toAfterTax: number;          // local currency of toCity
  requiredSalaryInToCity: number; // local currency of toCity
}

export interface CalculationResult {
  fromCity: City;
  toCity: City;
  fromAmount: number;          // weighted total in fromCity currency
  toAmount: number;            // weighted total in toCity currency
  fromCurrency: string;
  toCurrency: string;
  totalPercentChange: number;  // computed using USD-normalized totals
  breakdown: { [key: string]: CategoryBreakdown };
  lifestyleTier: string;
  salaryComparison: SalaryComparison;
}

export interface LifestyleDescription {
  label: string;
  summary: string;
  housing: string;
  food: string;
  transport: string;
  entertainment: string;
}

export interface TierAffordability {
  tier: LifestyleTier;
  label: string;
  totalCost: number;   // local currency of the destination city
  feasible: boolean;
  savings: number;     // local currency; 0 if not feasible
  shortfall: number;   // local currency; 0 if feasible
  // Per-category spend in local currency, or null when this tier isn't
  // affordable - there's no honest breakdown to show for a budget you can't
  // actually cover.
  breakdown: { [category: string]: number } | null;
}

export interface AffordabilityResult {
  city: City;
  monthlyIncome: number;       // in the city's local currency
  incomeCurrency: string;      // always city.currency - income has been converted
  tiers: TierAffordability[];  // ordered poor -> luxury
}

export type LifestyleTier = 'poor' | 'affordable' | 'above_average' | 'luxury';
type ExchangeRates = { [currency: string]: number };

const CATEGORIES = ['housing', 'food', 'transport', 'entertainment', 'utilities', 'healthcare'] as const;
const TIER_ORDER: LifestyleTier[] = ['poor', 'affordable', 'above_average', 'luxury'];

class CostCalculatorService {
  private data = costOfLivingData;
  private liveRates: LiveExchangeRates | null = null;
  private ratesReady: Promise<void>;

  constructor() {
    // Kick off the live FX fetch immediately; every conversion falls back
    // to the static snapshot in costOfLiving.json until (or unless) this
    // resolves, so the calculator is never blocked on network access.
    this.ratesReady = fetchLiveExchangeRates()
      .then((rates) => {
        if (rates) this.liveRates = rates;
      })
      .catch(() => undefined);
  }

  /** Resolves once the live-rate fetch has settled (success or fallback). */
  ensureRatesLoaded(): Promise<void> {
    return this.ratesReady;
  }

  /** True once live rates have actually been applied (vs. the static fallback). */
  hasLiveRates(): boolean {
    return this.liveRates !== null;
  }

  convertToUSD(income: number, incomeCurrency: string) {
    return this.convertCurrency(income, incomeCurrency, 'USD');
  }

  getCities(): City[] {
    return Object.values(this.data.cities) as unknown as City[];
  }

  getCityKeys(): string[] {
    return Object.keys(this.data.cities);
  }

  getLifestyleTiers(): { [key: string]: string } {
    return this.data.lifestyleTiers;
  }

  getLifestyleDescriptions(): Record<LifestyleTier, LifestyleDescription> {
    return this.data.lifestyleDescriptions as unknown as Record<LifestyleTier, LifestyleDescription>;
  }

  getExchangeRates(): ExchangeRates {
    return this.liveRates ?? (this.data.exchangeRates as ExchangeRates);
  }

  getExchangeRate(currency: string): number {
    const rates = this.getExchangeRates();
    const rate = rates[currency];
    return typeof rate === 'number' ? rate : 1;
  }

  convertCurrency(amount: number, fromCurrency: string, toCurrency: string): number {
    if (fromCurrency === toCurrency) return amount;
    const fromRate = this.getExchangeRate(fromCurrency);
    const toRate = this.getExchangeRate(toCurrency);
    if (!fromRate || !toRate) return amount;
    // rates are defined as: 1 unit of currency = rate USD
    // convert to USD then to target currency
    const usdAmount = amount * fromRate;
    return usdAmount / toRate;
  }

  calculateCostComparison(
    fromCityKey: string,
    toCityKey: string,
    lifestyleTier: LifestyleTier
  ): CalculationResult {
    const fromCity = this.data.cities[fromCityKey as keyof typeof this.data.cities] as unknown as City;
    const toCity = this.data.cities[toCityKey as keyof typeof this.data.cities] as unknown as City;
    if (!fromCity || !toCity) {
      throw new Error('Invalid city keys provided');
    }

    const breakdown: { [key: string]: CategoryBreakdown } = {};
    let fromTotalUSD = 0;
    let toTotalUSD = 0;

    for (const category of CATEGORIES) {
      const fromRange = fromCity.categories[category][lifestyleTier];
      const toRange = toCity.categories[category][lifestyleTier];

      // Use average of range for calculations
      const categoryFromAmount = (fromRange[0] + fromRange[1]) / 2;
      const categoryToAmount = (toRange[0] + toRange[1]) / 2;

      // Convert both to USD for comparison
      const fromAmountUSD = this.convertCurrency(categoryFromAmount, fromCity.currency, 'USD');
      const toAmountUSD = this.convertCurrency(categoryToAmount, toCity.currency, 'USD');

      fromTotalUSD += fromAmountUSD;
      toTotalUSD += toAmountUSD;

      const percentChange = fromAmountUSD > 0 ? ((toAmountUSD - fromAmountUSD) / fromAmountUSD) * 100 : 0;

      breakdown[category] = {
        fromCost: categoryFromAmount,
        toCost: categoryToAmount,
        fromCostUSD: fromAmountUSD,
        toCostUSD: toAmountUSD,
        percentChange
      };
    }

    // totals in USD -> percent change in USD
    const totalPercentChange = fromTotalUSD > 0 ? ((toTotalUSD - fromTotalUSD) / fromTotalUSD) * 100 : 0;

    // convert weighted USD totals back to each city's currency for display
    const totalFromAmount = this.convertCurrency(fromTotalUSD, 'USD', fromCity.currency);
    const totalToAmount = this.convertCurrency(toTotalUSD, 'USD', toCity.currency);

    // salary comparison: compute in USD, then convert back
    const fromSalaryUSD = this.convertCurrency(fromCity.averageSalary, fromCity.currency, 'USD');
    const toSalaryUSD = this.convertCurrency(toCity.averageSalary, toCity.currency, 'USD');
    const fromAfterTaxUSD = fromSalaryUSD * (1 - fromCity.taxRate);
    const toAfterTaxUSD = toSalaryUSD * (1 - toCity.taxRate);

    const costRatio = fromTotalUSD > 0 ? (toTotalUSD / fromTotalUSD) : 1;
    const requiredSalaryUSD = fromSalaryUSD * costRatio;
    const requiredSalaryInToCity = this.convertCurrency(requiredSalaryUSD, 'USD', toCity.currency);

    const salaryComparison: SalaryComparison = {
      fromSalary: fromCity.averageSalary,
      toSalary: toCity.averageSalary,
      fromAfterTax: this.convertCurrency(fromAfterTaxUSD, 'USD', fromCity.currency),
      toAfterTax: this.convertCurrency(toAfterTaxUSD, 'USD', toCity.currency),
      requiredSalaryInToCity
    };

    return {
      fromCity,
      toCity,
      fromAmount: totalFromAmount,
      toAmount: totalToAmount,
      fromCurrency: fromCity.currency,
      toCurrency: toCity.currency,
      totalPercentChange,
      breakdown,
      lifestyleTier,
      salaryComparison
    };
  }

  /**
   * Given a monthly income and a destination city, determines - per
   * lifestyle tier - whether that income actually covers the tier's real
   * costs there. Feasible tiers get a full category breakdown and leftover
   * savings; infeasible ones get a shortfall instead of a fabricated
   * breakdown, since there's nothing honest to show for a budget that
   * doesn't cover the bills.
   */
  calculateAffordability(cityKey: string, income: number, incomeCurrency: string): AffordabilityResult {
    const city = this.data.cities[cityKey as keyof typeof this.data.cities] as unknown as City;
    if (!city) {
      throw new Error('Invalid city key provided');
    }

    const incomeInCityCurrency = this.convertCurrency(income, incomeCurrency, city.currency);
    const tierLabels = this.getLifestyleTiers();

    const tiers: TierAffordability[] = TIER_ORDER.map((tier) => {
      const breakdown: { [category: string]: number } = {};
      let totalCost = 0;

      for (const category of CATEGORIES) {
        const range = city.categories[category][tier];
        const amount = (range[0] + range[1]) / 2;
        breakdown[category] = amount;
        totalCost += amount;
      }

      const feasible = incomeInCityCurrency >= totalCost;

      return {
        tier,
        label: tierLabels[tier] || tier,
        totalCost,
        feasible,
        savings: feasible ? incomeInCityCurrency - totalCost : 0,
        shortfall: feasible ? 0 : totalCost - incomeInCityCurrency,
        breakdown: feasible ? breakdown : null,
      };
    });

    return {
      city,
      monthlyIncome: incomeInCityCurrency,
      incomeCurrency: city.currency,
      tiers,
    };
  }
}

export default new CostCalculatorService();
